import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, Link2, Loader2, MapPin, MessageSquare, Send, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { useShareAIEdit, ShareEditBanner } from "@/hooks/useShareAIEdit";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function CommentList({ comments, onJump }) {
    if (!comments.length) return null;
    return (
        <div className="mt-3 space-y-2">
            {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-2 text-sm" data-testid="project-comment-item">
                    {onJump && (
                        <button type="button" onClick={() => onJump(c.timestamp_seconds || 0)}
                            className="shrink-0 px-1.5 py-0.5 rounded-sm bg-[#E5A93D]/15 text-[#8a6414] text-xs font-bold hover:bg-[#E5A93D]/30 transition">
                            {mmss(c.timestamp_seconds || 0)}
                        </button>
                    )}
                    <p className="text-zinc-700"><span className="font-bold text-zinc-950">{c.user_name}</span> — {c.text}</p>
                </div>
            ))}
        </div>
    );
}

function CommentBox({ onSubmit, withTimestamp, currentTime, busy }) {
    const [name, setName] = useState(() => localStorage.getItem("vsf_guest_name") || "");
    const [text, setText] = useState("");
    const submit = async () => {
        if (!name.trim()) { toast.error("Geef je naam op"); return; }
        if (!text.trim()) { toast.error("Schrijf een opmerking"); return; }
        localStorage.setItem("vsf_guest_name", name.trim());
        const ok = await onSubmit(name.trim(), text.trim());
        if (ok) setText("");
    };
    return (
        <div className="mt-4 p-3 rounded-sm border border-zinc-200 bg-zinc-50/70">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" /> Opmerking {withTimestamp && currentTime != null ? `op ${mmss(currentTime)}` : ""}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Je naam"
                    data-testid="project-comment-name"
                    className="h-9 px-3 rounded-sm border border-zinc-200 bg-white text-sm sm:w-40" />
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Schrijf je opmerking…"
                    data-testid="project-comment-text"
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    className="h-9 px-3 rounded-sm border border-zinc-200 bg-white text-sm flex-1" />
                <button type="button" onClick={submit} disabled={busy} data-testid="project-comment-submit"
                    className="h-9 px-4 rounded-sm bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800 transition inline-flex items-center gap-2 disabled:opacity-50">
                    <Send className="h-3.5 w-3.5" /> Verstuur
                </button>
            </div>
        </div>
    );
}

function VideoSection({ section, allowDownload, vertical }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const videoRef = useRef(null);
    const [curTime, setCurTime] = useState(0);
    const [quality, setQuality] = useState("hd");
    useEffect(() => {
        api.get(`/preview/${section.token}`).then(({ data }) => setData(data)).catch(() => {});
    }, [section.token]);
    if (!data) return null;
    const post = async (author_name, text) => {
        setBusy(true);
        try {
            const { data: c } = await api.post(`/preview/${section.token}/comments`, {
                author_name, text, timestamp_seconds: videoRef.current?.currentTime || 0,
            });
            setData((p) => ({ ...p, comments: [...(p.comments || []), c] }));
            toast.success("Opmerking geplaatst");
            setBusy(false);
            return true;
        } catch { toast.error("Plaatsen mislukt"); setBusy(false); return false; }
    };
    const jump = (t) => { if (videoRef.current) { videoRef.current.currentTime = t; videoRef.current.play(); } };
    const hasHd = !!data.stream_url;
    const activeSrc = quality === "uhd" || !hasHd ? data.video_url : data.stream_url;
    const switchQuality = (q) => {
        if (q === quality) return;
        const t = videoRef.current?.currentTime || 0;
        const wasPlaying = videoRef.current && !videoRef.current.paused;
        setQuality(q);
        setTimeout(() => {
            if (videoRef.current) {
                videoRef.current.currentTime = t;
                if (wasPlaying) videoRef.current.play().catch(() => {});
            }
        }, 350);
    };
    return (
        <section className="mb-12" data-testid={`project-section-${section.asset}`}>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                <span className="h-6 w-1.5 rounded-full bg-[#E5A93D]" /> {section.label}
                {hasHd && (
                    <span className="ml-auto inline-flex items-center rounded-full border border-zinc-300 overflow-hidden text-[11px] font-bold">
                        <button type="button" onClick={() => switchQuality("hd")}
                            className={`px-3 h-7 transition ${quality === "hd" ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 hover:text-zinc-900"}`}
                            data-testid={`project-quality-hd-${section.asset}`}>1080p</button>
                        <button type="button" onClick={() => switchQuality("uhd")}
                            className={`px-3 h-7 transition ${quality === "uhd" ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 hover:text-zinc-900"}`}
                            data-testid={`project-quality-4k-${section.asset}`}>4K</button>
                    </span>
                )}
            </h2>
            <div className={vertical ? "flex justify-center" : ""}>
                <video ref={videoRef} src={`${BACKEND}${activeSrc}`} controls playsInline preload="auto"
                    onTimeUpdate={(e) => setCurTime(e.target.currentTime)}
                    className={`rounded-sm bg-black ${vertical ? "max-h-[70vh] w-auto" : "w-full"}`}
                    data-testid={`project-video-${section.asset}`} />
            </div>
            {allowDownload && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <a href={`${BACKEND}/api/preview/${section.token}/download`}
                        className="inline-flex items-center gap-2 h-9 px-4 rounded-sm border border-zinc-300 text-sm font-bold text-zinc-800 hover:border-[#E5A93D] hover:bg-[#E5A93D]/10 transition"
                        data-testid={`project-download-${section.asset}`}>
                        <Download className="h-4 w-4" /> Download {section.label.toLowerCase()}
                    </a>
                    <button type="button"
                        onClick={async () => {
                            try {
                                const mp4Path = (data.stream_url || data.video_url || "").replace("/api/uploads/", "/api/media/");
                                await navigator.clipboard.writeText(`${BACKEND}${mp4Path}`);
                                toast.success(data.stream_url
                                    ? "MP4-link gekopieerd (1080p, streamvriendelijk) — plak hem in het videoveld van je website"
                                    : "MP4-link gekopieerd — plak hem in het videoveld van je website");
                            } catch { toast.error("Kopiëren mislukt"); }
                        }}
                        className="inline-flex items-center gap-2 h-9 px-4 rounded-sm border border-zinc-300 text-sm font-bold text-zinc-800 hover:border-[#E5A93D] hover:bg-[#E5A93D]/10 transition"
                        data-testid={`project-copy-mp4-${section.asset}`}>
                        <Link2 className="h-4 w-4" /> Kopieer MP4-link
                    </button>
                </div>
            )}
            <CommentBox onSubmit={post} withTimestamp currentTime={curTime} busy={busy} />
            <CommentList comments={data.comments || []} onJump={jump} />
        </section>
    );
}

function PhotosSection({ section, allowDownload }) {
    const [data, setData] = useState(null);
    const [lightbox, setLightbox] = useState(null); // photo index
    const [busy, setBusy] = useState(false);
    const [compare, setCompare] = useState(false);
    const [view, setView] = useState(null); // null = auto ("selection" indien aanwezig)
    const reload = () => api.get(`/preview/${section.token}`).then(({ data: d }) => setData(d)).catch(() => {});
    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section.token]);
    useEffect(() => { setCompare(false); }, [lightbox]);
    const touchRef = useRef(null);
    const onTouchStart = (e) => { touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
    const onTouchEnd = (e) => {
        const s = touchRef.current;
        touchRef.current = null;
        if (!s) return;
        const dx = e.changedTouches[0].clientX - s.x;
        const dy = e.changedTouches[0].clientY - s.y;
        if (Math.abs(dx) > 50 && Math.abs(dy) < 80) {
            if (dx < 0) navLightbox(1);
            else navLightbox(-1);
        }
    };
    const photos = data?.photos || [];
    const nPhotos = photos.length;
    // "Selectie website" — subset van de beste foto's (geordend); standaard eerst getoond
    const webSelection = data?.website_selection || [];
    const selIndices = webSelection.map((u) => photos.indexOf(u)).filter((i) => i >= 0);
    const hasSelection = selIndices.length > 0;
    const activeView = view ?? (hasSelection ? "selection" : "all");
    const visible = activeView === "selection" && hasSelection ? selIndices : photos.map((_, i) => i);
    const navLightbox = (dir) => setLightbox((cur) => {
        if (cur == null || visible.length === 0) return cur;
        const pos = visible.indexOf(cur);
        const npos = ((pos === -1 ? 0 : pos) + dir + visible.length) % visible.length;
        return visible[npos];
    });
    // AI-bewerken rechtstreeks op de totaalproject-link (zelfde flow als
    // op de fotoreeks-deellink: login/registreer-popup + eigen credits).
    const { handleEditClick, creditsBadge, modals } = useShareAIEdit({
        token: section.token,
        photos,
        refetch: reload,
        onBeforeOpen: () => setLightbox(null),
    });
    useEffect(() => {
        if (lightbox == null || !nPhotos) return;
        const onKey = (e) => {
            if (e.key === "ArrowRight") navLightbox(1);
            if (e.key === "ArrowLeft") navLightbox(-1);
            if (e.key === "Escape") setLightbox(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox == null, nPhotos, activeView, hasSelection]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!data) return null;
    const prevByIdx = data.photo_previous_urls || {};
    const aiCount = Object.keys(prevByIdx).length;
    const post = async (author_name, text) => {
        setBusy(true);
        try {
            const { data: c } = await api.post(`/preview/${section.token}/comments`, {
                author_name, text, timestamp_seconds: 0,
                photo_index: lightbox, photo_url: photos[lightbox],
            });
            setData((p) => ({ ...p, comments: [...(p.comments || []), c] }));
            toast.success("Opmerking geplaatst");
            setBusy(false);
            return true;
        } catch { toast.error("Plaatsen mislukt"); setBusy(false); return false; }
    };
    const photoComments = (i) => (data.comments || []).filter((c) => c.photo_index === i);
    return (
        <section className="mb-12" data-testid="project-section-photos">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h2 className="text-lg font-bold flex items-center gap-2">
                    <span className="h-6 w-1.5 rounded-full bg-[#E5A93D]" /> {section.label}
                    {hasSelection && activeView === "selection" && <span className="text-xs font-bold uppercase tracking-wider bg-[#E5A93D]/15 border border-[#E5A93D]/40 text-[#C8922C] rounded-sm px-2 py-0.5">Selectie</span>}
                    <span className="text-sm text-zinc-400 font-normal">({visible.length}{aiCount ? ` · ${aiCount} AI-bewerkt` : ""})</span>
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                    {hasSelection && (
                        <button
                            type="button"
                            onClick={() => setView(activeView === "selection" ? "all" : "selection")}
                            className="inline-flex items-center gap-2 h-9 px-4 rounded-sm border border-zinc-300 text-sm font-bold text-zinc-800 hover:border-[#E5A93D] hover:bg-[#E5A93D]/10 transition whitespace-nowrap"
                            data-testid="project-photos-view-toggle"
                        >
                            {activeView === "selection" ? `Bekijk al de foto's (${photos.length})` : `Bekijk selectie website (${selIndices.length})`}
                        </button>
                    )}
                    {allowDownload && hasSelection && (
                        <a href={`${BACKEND}/api/preview/${section.token}/photos-zip?selection=1`}
                            className="inline-flex items-center gap-2 h-9 px-4 rounded-sm border border-[#E5A93D]/60 bg-[#E5A93D]/10 text-sm font-bold text-[#9a6f1f] hover:bg-[#E5A93D]/20 transition whitespace-nowrap"
                            data-testid="project-download-selection">
                            <Download className="h-4 w-4" />
                            <span className="hidden sm:inline">Download selectie ({selIndices.length}) ZIP</span>
                            <span className="sm:hidden">Selectie (ZIP)</span>
                        </a>
                    )}
                {allowDownload && (
                    <a href={`${BACKEND}/api/preview/${section.token}/photos-zip`}
                        className="inline-flex items-center gap-2 h-9 px-4 rounded-sm border border-zinc-300 text-sm font-bold text-zinc-800 hover:border-[#E5A93D] hover:bg-[#E5A93D]/10 transition whitespace-nowrap"
                        data-testid="project-download-photos">
                        <Download className="h-4 w-4" />
                        <span className="hidden sm:inline">Download alle foto's (ZIP)</span>
                        <span className="sm:hidden">Download (ZIP)</span>
                    </a>
                )}
                </div>
            </div>
            {photos.length > 0 && <ShareEditBanner right={creditsBadge} />}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {visible.map((i) => { const p = photos[i]; return (
                    <button key={`${p}-${i}`} type="button" onClick={() => setLightbox(i)} data-testid="project-photo-thumb"
                        className="relative aspect-[4/3] rounded-sm overflow-hidden group">
                        <img src={`${BACKEND}${p}`} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                        {prevByIdx[String(i)] && (
                            <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-sm bg-[#E5A93D] text-black text-[10px] font-bold flex items-center gap-1" title="Bewerkt met AI — klik om te vergelijken met origineel">
                                ✦ AI
                            </span>
                        )}
                        {photoComments(i).length > 0 && (
                            <span className="absolute top-1.5 right-1.5 h-5 min-w-5 px-1 rounded-full bg-[#E5A93D] text-black text-[10px] font-bold flex items-center justify-center">
                                {photoComments(i).length}
                            </span>
                        )}
                    </button>
                ); })}
            </div>
            {lightbox != null && (
                <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="project-lightbox">
                    <button type="button" data-testid="project-photo-prev"
                        onClick={(e) => { e.stopPropagation(); navLightbox(-1); }}
                        className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center rounded-full bg-white/15 hover:bg-[#E5A93D] text-white hover:text-black transition backdrop-blur-sm">
                        <ChevronLeft className="h-7 w-7" />
                    </button>
                    <button type="button" data-testid="project-photo-next"
                        onClick={(e) => { e.stopPropagation(); navLightbox(1); }}
                        className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center rounded-full bg-white/15 hover:bg-[#E5A93D] text-white hover:text-black transition backdrop-blur-sm">
                        <ChevronRight className="h-7 w-7" />
                    </button>
                    <div className="max-w-4xl w-full bg-white rounded-sm p-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-bold flex items-center gap-2">
                                Foto {(visible.indexOf(lightbox) >= 0 ? visible.indexOf(lightbox) : 0) + 1} / {visible.length}
                                {prevByIdx[String(lightbox)] && <span className="px-1.5 py-0.5 rounded-sm bg-[#E5A93D] text-black text-[10px] font-bold">✦ AI</span>}
                            </p>
                            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
                                <button type="button" data-testid="project-lightbox-edit-btn"
                                    onClick={(e) => { e.stopPropagation(); handleEditClick(lightbox); }}
                                    title="Bewerk deze foto met AI — credits worden afgeboekt"
                                    className="h-8 px-2.5 sm:px-3 rounded-sm text-xs font-bold bg-[#E5A93D] hover:bg-[#C8922C] text-black transition inline-flex items-center gap-1.5 whitespace-nowrap">
                                    ✦ <span className="hidden sm:inline">Bewerk met AI</span><span className="sm:hidden">Bewerk</span>
                                </button>
                                {prevByIdx[String(lightbox)] && (
                                    <button type="button" onClick={() => setCompare((c) => !c)} data-testid="project-compare-toggle"
                                        className={`h-8 px-2.5 sm:px-3 rounded-sm text-xs font-bold border transition whitespace-nowrap ${compare ? "bg-[#E5A93D] text-black border-[#E5A93D]" : "border-zinc-300 text-zinc-700 hover:border-[#E5A93D]"}`}>
                                        <span className="hidden sm:inline">Vergelijk met origineel</span><span className="sm:hidden">{compare ? "Nieuw" : "Origineel"}</span>
                                    </button>
                                )}
                                {allowDownload && (() => {
                                    const prevUrl = prevByIdx[String(lightbox)];
                                    const mkHref = (url, label) => {
                                        const orig = url.split("/").pop().split("?")[0];
                                        const ext = orig.includes(".") ? orig.slice(orig.lastIndexOf(".")) : ".jpg";
                                        const address = (data?.title || data?.subtitle || "foto").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_");
                                        const nice = `${label ? label + " - " : ""}${address} - foto ${String(lightbox + 1).padStart(2, "0")}${ext}`;
                                        return `${BACKEND}/api/files/${encodeURIComponent(orig)}?name=${encodeURIComponent(nice)}`;
                                    };
                                    return (
                                        <>
                                            <a href={mkHref(photos[lightbox], prevUrl ? "AI" : "")} download data-testid="project-lightbox-download-current"
                                                onClick={(e) => e.stopPropagation()}
                                                title={prevUrl ? "Download de AI-bewerkte foto" : "Download deze foto"}
                                                className="h-8 px-2.5 sm:px-3 rounded-sm text-xs font-bold border border-zinc-300 text-zinc-700 hover:border-[#E5A93D] hover:bg-[#E5A93D]/10 transition inline-flex items-center gap-1.5 whitespace-nowrap">
                                                <Download className="h-3.5 w-3.5" />
                                                <span className="hidden sm:inline">{prevUrl ? "AI foto" : "Download"}</span><span className="sm:hidden">{prevUrl ? "AI" : "⬇"}</span>
                                            </a>
                                            {prevUrl && (
                                                <a href={mkHref(prevUrl, "Origineel")} download data-testid="project-lightbox-download-original"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title="Download de originele foto (voor AI-bewerking)"
                                                    className="h-8 px-2.5 sm:px-3 rounded-sm text-xs font-bold border border-zinc-300 text-zinc-700 hover:border-[#E5A93D] hover:bg-[#E5A93D]/10 transition inline-flex items-center gap-1.5 whitespace-nowrap">
                                                    <Download className="h-3.5 w-3.5" />
                                                    <span className="hidden sm:inline">Originele foto</span><span className="sm:hidden">Orig.</span>
                                                </a>
                                            )}
                                        </>
                                    );
                                })()}
                                <button type="button" onClick={() => setLightbox(null)} data-testid="project-lightbox-close"><X className="h-5 w-5 text-zinc-500" /></button>
                            </div>
                        </div>
                        {compare && prevByIdx[String(lightbox)] ? (
                            <BeforeAfterSlider
                                beforeUrl={`${BACKEND}${prevByIdx[String(lightbox)]}`}
                                afterUrl={`${BACKEND}${photos[lightbox]}`}
                                beforeLabel="Origineel"
                                afterLabel="AI"
                                testId="project-before-after-slider"
                            />
                        ) : (
                            <img key={lightbox} src={`${BACKEND}${photos[lightbox]}`} alt=""
                                className="w-full max-h-[55vh] object-contain rounded-sm bg-zinc-100 select-none"
                                onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
                                draggable={false} data-testid="project-lightbox-img" />
                        )}
                        <div className="sm:hidden flex items-center justify-center gap-5 mt-3" data-testid="project-photo-nav-sm">
                            <button type="button" data-testid="project-photo-prev-sm" className="h-10 w-10 rounded-sm border border-zinc-200 flex items-center justify-center active:bg-zinc-100" onClick={() => navLightbox(-1)}><ChevronLeft className="h-5 w-5" /></button>
                            <span className="text-xs font-bold text-zinc-500">{(visible.indexOf(lightbox) >= 0 ? visible.indexOf(lightbox) : 0) + 1} / {visible.length}</span>
                            <button type="button" data-testid="project-photo-next-sm" className="h-10 w-10 rounded-sm border border-zinc-200 flex items-center justify-center active:bg-zinc-100" onClick={() => navLightbox(1)}><ChevronRight className="h-5 w-5" /></button>
                        </div>
                        <CommentBox onSubmit={post} busy={busy} />
                        <CommentList comments={photoComments(lightbox)} />
                    </div>
                </div>
            )}
            {modals}
        </section>
    );
}

export default function PublicProject() {
    const { token } = useParams();
    const [proj, setProj] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get(`/project/${token}`).then(({ data }) => setProj(data))
            .catch((e) => setErr(e.response?.data?.detail || "Deze link is niet meer geldig"));
    }, [token]);
    if (err) {
        return (
            <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6">
                <p className="text-zinc-600 text-lg" data-testid="project-error">{err}</p>
            </div>
        );
    }
    if (!proj) {
        return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E5A93D]" /></div>;
    }
    return (
        <div className="min-h-screen bg-[#FAFAF8] text-zinc-950" data-testid="public-project-page">
            <header className="border-b border-zinc-200 bg-white">
                <div className="max-w-4xl mx-auto px-5 py-8 flex items-start justify-between gap-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-[#B8860B] font-bold mb-2">Vastgoed Social Factory · Volledig project</p>
                        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="project-title">{proj.title}</h1>
                        <p className="mt-2 text-sm text-zinc-500 flex items-center gap-1.5">
                            <MapPin className="h-4 w-4 text-[#E5A93D]" />
                            {proj.company || proj.makelaar || "Professionele vastgoedpresentatie"}
                        </p>
                    </div>
                    <a href="https://vastgoed.social-factory.be" target="_blank" rel="noopener noreferrer"
                        data-testid="project-header-logo-link" className="shrink-0 hover:opacity-75 transition">
                        <img src="/social-factory-logo.png" alt="Social Factory" data-testid="project-header-logo"
                            className="h-20 sm:h-28 w-auto invert" />
                    </a>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-5 py-10">
                {proj.sections.map((s) => s.asset === "photos"
                    ? <PhotosSection key={s.asset} section={s} allowDownload={proj.allow_download} />
                    : <VideoSection key={s.asset} section={s} allowDownload={proj.allow_download} vertical={s.asset === "reel"} />)}
                <footer className="pt-6 border-t border-zinc-200 text-center text-xs text-zinc-400">
                    Gedeeld via <span className="font-bold text-zinc-600">Vastgoed Social Factory</span> — professionele vastgoedvideo & fotografie
                </footer>
            </main>
        </div>
    );
}
